import React, { Children, useState, useMemo } from 'react';
import cn from 'classnames';

import Spinner from 'components/spinner';

import { WidgetTileEmptyPlaceholder } from './components/placeholder/empty/empty';
import TileHeader from './components/tile-header';
import TileSwitchList from './components/tile-switch-list';

import type { TileEmptyPlaceholderProps, TileProps } from './tile.props';

import classes from './tile.module.scss';

const spinnerStyle = {
  width: '50px',
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 97
} as const;

export const WidgetTile = ({
  title,
  titleIcon,
  children,
  emptyPlaceholder,
  className,
  onClick,
  onTitleClick,
  childrenClassName,
  isLoading,
  id,
  titleLink
}: TileProps) => {
  const [activeSliderIndx, setActiveSliderIndx] = useState(0);

  const currentEmptyPlaceholder = useMemo(() => {
    if (Array.isArray(emptyPlaceholder)) {
      return emptyPlaceholder[activeSliderIndx] ?? ({} as TileEmptyPlaceholderProps);
    }
    return emptyPlaceholder ?? ({} as TileEmptyPlaceholderProps);
  }, [emptyPlaceholder, activeSliderIndx]);

  const childrenArray = Children.toArray(children);
  const activeChild = childrenArray[activeSliderIndx];

  return (
    <div id={id} className={cn(classes.container, className, { pointer: onClick })} onClick={onClick}>
      {isLoading && (
        <div className={classes.spinner_container}>
          <Spinner style={spinnerStyle} />
        </div>
      )}

      {currentEmptyPlaceholder.isEmpty ? (
        <WidgetTileEmptyPlaceholder label={currentEmptyPlaceholder.text} icon={currentEmptyPlaceholder.icon} />
      ) : (
        <>
          {title && (
            <TileHeader
              title={title}
              titleIcon={titleIcon}
              titleLink={titleLink}
              onTitleClick={onTitleClick}
              activeSliderIndx={activeSliderIndx}
            />
          )}

          <div className={cn(title ? classes.children_with_title : classes.children, childrenClassName)}>
            {activeChild}
          </div>
        </>
      )}

      {childrenArray.length > 1 && (
        <TileSwitchList
          childrenCount={childrenArray.length}
          activeIndex={activeSliderIndx}
          onSwitchChange={setActiveSliderIndx}
          id={id}
        />
      )}
    </div>
  );
};
